import { useEffect, useState, useMemo } from "react";
import { DashboardStats, dashbordStatsService } from "../../services/dashbordStatsService";
import useToken from "../../../../hooks/auth/useToken";
import usePayments from "../payments/usePayments";
import useUsers from "../users/useUsers";
import useCountries from "../countries/useCountries";

export interface EnhancedDashboardStats {
  // Paiements
  totalPayments: number;
  totalSuccessPayments: number;
  totalErrorPayments: number;
  totalPendingPayments: number;
  percentageOfSuccessPayments: number;
  // Utilisateurs
  totalUsers: number;
  totalVerifiedUsers: number;
  totalUnverifiedUsers: number;
  totalActiveUsers: number;
  // Pays
  totalCountries: number;
  totalCountriesTransfert: number;
  // Finance
  totalRevenueCad: number;
  totalRevenueFcfaCmr: number;
  // Journalier
  totalSuccessPaymentsToday: number;
  totalErrorPaymentsToday: number;
  // Stats fusionnées
  paymentsTodayTotal: number;
  revenueToday: number;
  totalFees: number;
  topOperatorsFormatted: Array<[string, number]>;
}

export const useDashboardStats = () => {
  const [apiStats, setApiStats] = useState<DashboardStats | null>(null);
  const [apiLoading, setApiLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { token } = useToken();

  // Récupération des données locales
  const { payments, paymentsLoading } = usePayments({});

  const { users, usersLoading } = useUsers({});
  const { countries, countriesLoading } = useCountries({});

  // Chargement des stats API
  useEffect(() => {
    if (!token) return;

    const fetchStats = async () => {
      try {
        setApiLoading(true);
        setApiError(null);
        const data = await dashbordStatsService.getStats(token);
        setApiStats(data);
      } catch (err: any) {
        setApiError(err.message ?? "Erreur lors du chargement des statistiques");
      } finally {
        setApiLoading(false);
      }
    };

    fetchStats();
  }, [token]);

  // Calculs locaux
  const localStats = useMemo(() => {
    if (!payments || !users || !countries) return null;

    const succeededPayments = payments.filter((p) => p.status === "SUCCEEDED");
    const failedPayments = payments.filter((p) => p.status === "FAILED");
    const processingPayments = payments.filter((p) => p.status === "PROCESSING");

    const verifiedUsers = users.filter((u) => u.is_verified === "YES");
    const activeUsers = users.filter((u) => u.status === "ACTIVE");
    const unverifiedUsers = users.filter(
      (u) => u.is_verified === "NO" || u.is_verified === "PENDING" || u.is_verified === "REJECTED"
    );

    const totalRevenue = succeededPayments.reduce((sum, p) => sum + p.total_cad, 0);
    const totalFees = succeededPayments.reduce((sum, p) => sum + p.fee_cad, 0);
    const successRate = payments.length > 0 ? Math.round((succeededPayments.length / payments.length) * 100) : 0;

    const totalFcfa = succeededPayments.reduce((sum, p) => sum + p.amount_fcfa, 0);

    // Paiements aujourd'hui
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const paymentsToday = payments.filter((p) => new Date(p.created_at) >= today);
    const successPaymentsToday = paymentsToday.filter((p) => p.status === "SUCCEEDED");
    const errorPaymentsToday = paymentsToday.filter((p) => p.status === "FAILED");
    const revenueToday = successPaymentsToday.reduce((sum, p) => sum + p.total_cad, 0);

    // Top opérateurs
    const operatorCounts = payments.reduce(
      (acc: Record<string, number>, p) => {
        acc[p.operator] = (acc[p.operator] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
    const topOperatorsLocal = Object.entries(operatorCounts)
      .sort(([, a], [, b]) => (b as number) - (a as number))
      .slice(0, 3);

    return {
      totalPayments: payments.length,
      totalSuccessPayments: succeededPayments.length,
      totalErrorPayments: failedPayments.length,
      totalPendingPayments: processingPayments.length,
      percentageOfSuccessPayments: successRate,
      totalUsers: users.length,
      totalVerifiedUsers: verifiedUsers.length,
      totalUnverifiedUsers: unverifiedUsers.length,
      totalActiveUsers: activeUsers.length,
      totalCountries: countries.length,
      totalCountriesTransfert: 0,
      totalRevenueCad: totalRevenue,
      totalRevenueFcfaCmr: totalFcfa,
      totalSuccessPaymentsToday: successPaymentsToday.length,
      totalErrorPaymentsToday: errorPaymentsToday.length,
      paymentsTodayTotal: paymentsToday.length,
      revenueToday: revenueToday,
      totalFees: totalFees,
      topOperatorsFormatted: topOperatorsLocal,
    };
  }, [payments, users, countries]);

  // Fusion des données : API prioritaire, sinon local
  const enhancedStats = useMemo<EnhancedDashboardStats | null>(() => {
    if (!localStats) return null;

    // Si l'API est en cours de chargement, on retourne les stats locales
    if (apiLoading) return localStats;

    // Si l'API a des données, on fusionne champ par champ
    if (apiStats) {
      return {
        // Paiements
        totalPayments: apiStats.paiment?.total_payments ?? localStats.totalPayments,
        totalSuccessPayments: apiStats.dayly_finance?.total_success_payment_today ?? localStats.totalSuccessPayments,
        totalErrorPayments: apiStats.dayly_finance?.total_error_payment_today ?? localStats.totalErrorPayments,
        totalPendingPayments: apiStats.paiment?.total_pending_payments ?? localStats.totalPendingPayments,
        percentageOfSuccessPayments:
          apiStats.paiment?.percentage_of_success_payments ?? localStats.percentageOfSuccessPayments,

        // Utilisateurs
        totalUsers: apiStats.users?.total_users ?? localStats.totalUsers,
        totalVerifiedUsers: apiStats.users?.totals_verified_users ?? localStats.totalVerifiedUsers,
        totalUnverifiedUsers: apiStats.users?.totals_unverified_users ?? localStats.totalUnverifiedUsers,
        totalActiveUsers: apiStats.users?.totals_active_users ?? localStats.totalActiveUsers,

        // Pays
        totalCountries: apiStats.country?.total_country ?? localStats.totalCountries,
        totalCountriesTransfert: apiStats.country?.total_country_transfert ?? localStats.totalCountriesTransfert,

        // Finance
        totalRevenueCad: apiStats.finance?.revenu_total_cad ?? localStats.totalRevenueCad,
        totalRevenueFcfaCmr: apiStats.finance?.revenu_total_fcfa_cmr ?? localStats.totalRevenueFcfaCmr,

        // Journalier
        totalSuccessPaymentsToday:
          apiStats.dayly_finance?.total_success_payment_today ?? localStats.totalSuccessPaymentsToday,
        totalErrorPaymentsToday:
          apiStats.dayly_finance?.total_error_payment_today ?? localStats.totalErrorPaymentsToday,

        // Stats fusionnées (toujours des calculs locaux car pas dans l'API)
        paymentsTodayTotal:
          (apiStats.dayly_finance?.total_success_payment_today ?? localStats.totalSuccessPaymentsToday) +
          (apiStats.dayly_finance?.total_error_payment_today ?? localStats.totalErrorPaymentsToday),
        revenueToday: localStats.revenueToday,
        totalFees: localStats.totalFees,

        // Top opérateurs : on prend ceux de l'API s'ils existent et non vides
        topOperatorsFormatted:
          apiStats.top_operators && apiStats.top_operators.length > 0
            ? apiStats.top_operators.map((op) => [op.operator, op.transaction_count] as [string, number])
            : localStats.topOperatorsFormatted,
      };
    }

    // Si pas d'API stats, retourner les stats locales
    return localStats;
  }, [apiStats, localStats, apiLoading]);

  return {
    stats: enhancedStats,
    loading: paymentsLoading || usersLoading || countriesLoading || apiLoading,
    error: apiError,
  };
};
